import {StudentsByApp, UninitializedStudent} from "@/app/types/Student";
import UserInfo from "@/app/types/UserInfo";
import {ConsultationRequest} from "@/lib/partner-api/addConsultation";
import {Key} from "react-aria-components";

export type ContactFormParentFields = {
	parentFirstName: string;
	parentLastName: string;
	parentEmail: string;
	parentPhoneNumber: string;
};

/**
 * This is an object field for now.  If we wanted to select arbitrary campuses
 * we may want to make this an array, but it would have implications for the UI
 * side of things to do it that way, so I'm leaving it as an object for now.
 */
export type ContactFormSelectedCampuses = {
	primaryCampus: Key;
	secondaryCampus?: Key;
};

export type ContactFormSelectedCampusHandlers = {
	setPrimaryCampus: (newPrimaryCampus: Key) => void;
	setSecondaryCampus: (newSecondaryCampus: Key) => void;
	resetCampusSelection: () => void;
};

export type ContactFormPlatformStudent = {
	studentPlatformId: string;
	studentName: string;
};

export type ContactFormSelectedStudents = {
	initialStudentName: string;
	newStudentNames: Record<number, string>;
	selectedAcademyStudents: string[];
	selectedPlatformStudents: string[];
	platformStudentNames: Record<string, string>;
};

export type ContactFormStudentHandlers = {
	onInitialStudentChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	setNewStudent: (key: number, value: string) => void;
	setNewStudents: (newStudents: Record<number, string>) => void;
	setSelectedAcademyStudents: (selection: string[]) => void;
	setSelectedPlatformStudents: (selection: string[]) => void;
	onPlatformStudentNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	resetStudentFields: () => void;
};

export type ContactFormState = {
	selectedCampuses: ContactFormSelectedCampuses;
	parentFields: ContactFormParentFields;
	studentFields: ContactFormSelectedStudents;
	isOptedIntoEmails: boolean;
};

export const composeConsultationRequest: (
	formState: ContactFormState,
	userInfo?: UserInfo | null,
	clientDeviceId?: string,
	userStudents?: StudentsByApp
) => ConsultationRequest = (
	{parentFields, studentFields, selectedCampuses, isOptedIntoEmails},
	userInfo = null,
	clientDeviceId,
	userStudents
) => {
	// concat the initial student field with the additional new student fields (if they exist)
	const allNewStudents = [
		studentFields.initialStudentName ?? "",
		...Object.values(studentFields.newStudentNames ?? {}),
	].filter((studentName) => studentName);
	const existingStudents = userStudents?.academyStudents.filter((a) =>
		studentFields.selectedAcademyStudents.includes(a.userId)
	);
	return {
		parentFirstName: userInfo?.first_name || parentFields.parentFirstName,
		parentLastName: userInfo?.last_name || parentFields.parentLastName,
		parentEmail: userInfo?.email || parentFields.parentEmail,
		parentPhoneNumber: parentFields.parentPhoneNumber,
		campusId: selectedCampuses.primaryCampus.toString(),
		additionalCampusId: selectedCampuses.secondaryCampus?.toString(),
		newStudentNames: allNewStudents,
		existingStudents,
		uninitializedStudents: studentFields.selectedPlatformStudents?.map(
			(studentId) => {
				return {
					platformId: studentId,
					firstName: studentFields.platformStudentNames[studentId],
				};
			}
		),
		// TODO: make this a boolean param instead
		optin: isOptedIntoEmails ? "true" : "",
		cdid: clientDeviceId,
	};
};

export const hasSelectedChildren = (request: ConsultationRequest) => {
	// check for children
	if (
		!request.existingStudents?.length &&
		!request.uninitializedStudents?.length &&
		!request.newStudentNames?.length
	) {
		return false;
	}

	return true;
};

/**
 * This mostly exists to translate the types for the duplicate check to match the
 * client-side check. This one is called from the post action in useContactForm.
 * validateNoStudentNameDuplicates is called from ContactFormStudentFields and does the actual checks.
 */
export const hasDuplicateChildren = (request: ConsultationRequest) => {
	// Check for duplicate first names using the existing validation function
	const studentData = {
		initialStudentName: "",
		newStudentNames:
			request.newStudentNames?.reduce(
				(acc, name, index) => {
					if (name.trim()) {
						acc[index] = name;
					}
					return acc;
				},
				{} as Record<number, string>
			) || {},
		selectedPlatformStudents:
			request.uninitializedStudents?.map((s) => s.platformId) || [],
		platformStudents:
			request.uninitializedStudents?.map((s) => ({
				platformId: s.platformId,
				displayName: s.firstName,
				firstName: s.firstName,
				lastName: undefined,
			})) || [],
		platformStudentNames:
			request.uninitializedStudents?.reduce(
				(acc, s) => {
					acc[s.platformId] = s.firstName;
					return acc;
				},
				{} as Record<string, string>
			) || {},
		selectedAcademyStudents:
			request.existingStudents?.map((s) => s.userId) || [],
		academyStudents: request.existingStudents?.map((s) => ({
			userId: s.userId,
			firstName: s.firstName,
			lastName: s.lastName,
		})),
	};

	// This function is positive (hasDuplicate) so we need to negate it to get the correct result
	return !validateNoStudentNameDuplicates(studentData);
};

/**
 * Takes all student name fields and checks for duplicate values across all of them
 * @param studentData Object containing all student-related data
 * @returns true if there are no duplicates, false if duplicates are found
 */
export const validateNoStudentNameDuplicates = (studentData: {
	initialStudentName: string;
	newStudentNames: Record<number, string>;
	selectedPlatformStudents: string[];
	platformStudents: UninitializedStudent[] | undefined;
	platformStudentNames?: Record<string, string>;
	selectedAcademyStudents: string[];
	academyStudents?: Array<{
		userId: string;
		firstName: string;
		lastName: string;
	}>;
}) => {
	const {
		initialStudentName,
		newStudentNames,
		selectedPlatformStudents,
		platformStudents,
		platformStudentNames,
		selectedAcademyStudents,
		academyStudents,
	} = studentData;

	// Collect all non-empty student names
	const allNames: string[] = [];

	// Add initial student name if not empty
	if (initialStudentName.trim()) {
		allNames.push(initialStudentName.trim().toLowerCase());
	}

	// Add new student names if not empty
	Object.values(newStudentNames).forEach((name) => {
		if (name.trim()) {
			allNames.push(name.trim().toLowerCase());
		}
	});

	// Add platform student names if not empty
	if (platformStudents && selectedPlatformStudents.length > 0) {
		selectedPlatformStudents.forEach((studentId) => {
			// Use the user-entered name from platformStudentNames if available, otherwise fall back to displayName
			const studentName =
				platformStudentNames?.[studentId] ||
				platformStudents.find((s) => s.platformId === studentId)?.displayName;
			if (studentName?.trim()) {
				allNames.push(studentName.trim().toLowerCase());
			}
		});
	}

	// Add selected academy student names if not empty
	if (academyStudents) {
		selectedAcademyStudents.forEach((studentId) => {
			const student = academyStudents.find((s) => s.userId === studentId);
			if (student?.firstName?.trim()) {
				allNames.push(student.firstName.trim().toLowerCase());
			}
		});
	}

	// Check for duplicates
	const uniqueNames = new Set(allNames);
	return allNames.length === uniqueNames.size;
};
